<template>
  <div class="max-w-3xl mx-auto mb-3 sm:mb-4 px-2">
    <!-- Step Indicators - Horizontally scrollable on mobile -->
    <nav aria-label="Progress" class="step-nav-container mb-2 sm:mb-3">
      <div class="step-nav">
        <ol class="step-nav-inner">
          <li v-for="(step, index) in steps" :key="index" class="flex items-center flex-shrink-0">
            <span 
              :class="[
                'flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all duration-300 border text-xs sm:text-sm whitespace-nowrap',
                currentStepIndex >= index 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-600 shadow-md' 
                  : 'bg-white text-gray-500 border-gray-300'
              ]"
            >
              <svg v-if="currentStepIndex > index" class="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="font-medium">{{ step }}</span>
            </span>
            <svg v-if="index < steps.length - 1" class="w-3 h-3 sm:w-4 sm:h-4 mx-1 sm:mx-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </li>
        </ol>
      </div>
    </nav>
    
    <!-- Progress Bar -->
    <div class="bg-white rounded-full h-2.5 sm:h-3 md:h-4 shadow-inner overflow-hidden border border-gray-300">
      <div
        :class="progressBarColor"
        class="h-full rounded-full transition-all duration-700 ease-out shadow-lg relative overflow-hidden"
        :style="{ width: progressWidth }"
        role="progressbar"
        :aria-valuenow="currentStep"
        aria-valuemin="0"
        :aria-valuemax="totalSteps"
      >
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
      </div>
    </div>
    
    <!-- Progress Text -->
      <div class="flex justify-between mt-2 sm:mt-3 text-xs sm:text-sm text-gray-600 px-1">
        <span>{{ $t('progressBar.step', { current: currentStep, total: totalSteps }) }}</span>
        <span class="font-medium">{{ $t('progressBar.complete', { percent: progressPercent }) }}</span>
      </div>
  </div>
</template>

<script>
export default {
  name: 'ProgressBar',
  props: {
    steps: {
      type: Array,
      required: true
    },
    currentPage: {
      type: Number,
      required: true
    },
    totalSteps: {
      type: Number,
      default: 7
    }
  },
  computed: {
    currentStepIndex() {
      const pageToStep = {
        '-2': 0, '-1': 0, '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6
      };
      return pageToStep[this.currentPage] || 0;
    },
    currentStep() {
      return Math.max(1, this.currentPage + 2);
    },
    progressWidth() {
      return `${((this.currentPage + 2) / this.totalSteps) * 100}%`;
    },
    progressPercent() {
      return Math.round(((this.currentPage + 2) / this.totalSteps) * 100);
    },
    progressBarColor() {
      const colors = {
        '-2': 'bg-gradient-to-r from-indigo-500 to-purple-600',
        '-1': 'bg-gradient-to-r from-blue-500 to-blue-600',
        '0': 'bg-gradient-to-r from-green-500 to-emerald-600',
        '1': 'bg-gradient-to-r from-blue-500 to-indigo-600',
        '2': 'bg-gradient-to-r from-yellow-500 to-orange-600',
        '3': 'bg-gradient-to-r from-red-500 to-pink-600',
        '4': 'bg-gradient-to-r from-orange-500 to-yellow-600',
        '5': 'bg-gradient-to-r from-purple-500 to-indigo-600',
        '6': 'bg-gradient-to-r from-green-500 to-emerald-600',
      };
      return colors[this.currentPage] || 'bg-gradient-to-r from-blue-500 to-blue-600';
    }
  }
}
</script>

<style scoped>
.step-nav-container {
  width: 100%;
}

.step-nav {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
}

.step-nav::-webkit-scrollbar {
  display: none;
}

.step-nav-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: max-content;
  padding: 0 4px;
}

/* On small screens, allow scrolling */
@media (max-width: 640px) {
  .step-nav-inner {
    justify-content: flex-start;
  }
}
</style>
